import { createSlice } from "@reduxjs/toolkit";
import { getCookie, removeCookie, setCookie } from "../../Utils/cookiesHelper";
import { jwtToken, fetchAllMessages, fetchMessages } from "../Thunks/ChatSocketThunk";
import { messageTime } from "../../Utils/messageTime";
import { debugLog } from "../../Utils/utils";
import { carriedProducts } from "../../Utils/productLinks";

// The products an answer came with live only in this copy of it. The server
// keeps the answer's words but not its product list (checked against
// /v1/message/by-chat on 4 Oct 2026: no products field), so a reload of the
// conversation used to swap in a copy without them, and the "I found N
// results" strip under the answer vanished seconds after it appeared. The list
// already received is kept for the same message.
const keepCarriedProducts = (previous, reloaded) => {
  const byId = new Map();
  (previous || []).forEach((message) => {
    const id = message && (message.messageId || message._id);
    if (id != null && carriedProducts(message).length) byId.set(String(id), message.products);
  });
  if (!byId.size) return reloaded;
  return reloaded.map((message) => {
    const id = message && (message.messageId || message._id);
    if (id == null || carriedProducts(message).length) return message;
    const kept = byId.get(String(id));
    return kept ? { ...message, products: kept } : message;
  });
};

const ChatSocketSlice = createSlice({
  name: "chatSocket",
  initialState: {
    jwtToken: getCookie("jwtToken") || null,
    socketConnection: false,
    isTyping: false,
    messages: [],
    agentsOnline: [],
    streamingMessage: null,
    messagesPage: 0,
    hasMoreMessages: true,
    loadingMoreMessages: false,
    // Counts lists that were fetched rather than spoken. Only ever compared
    // with its own previous value — see loadMessages.
    historyLoads: 0,
  },
  reducers: {
    startTyping: (state, action) => {
      state.isTyping = true;
    },
    stopTyping: (state, action) => {
      state.isTyping = false;
    },
    socketConnected: (state, action) => {
      state.socketConnection = true;
      debugLog("WebSocket connection established");
    },
    socketDisconnected: (state, action) => {
      state.socketConnection = false;
      debugLog("WebSocket connection lost");
    },
    checkConnectionStatus: (state) => {
      // This will be called to check if we need to establish connection
      debugLog("Checking WebSocket connection:", state.socketConnection);
    },
    addNewMessage: (state, action) => {
      const newMessage = action.payload;

      // Check if message already exists by messageId or content
      const isMessageExist = state.messages.find(
        (message) =>
          message.messageId === newMessage.messageId ||
          (message.content === newMessage.content &&
            Math.abs(
              messageTime(message.createdAt || message.created_at || 0) -
                messageTime(newMessage.createdAt || newMessage.created_at || 0)
            ) < 1000)
      );

      if (!isMessageExist) {
        // Ensure the message has a proper ID for React key
        if (!newMessage._id && newMessage.messageId) {
          newMessage._id = newMessage.messageId;
        }

        // Add createdAt if missing
        if (!newMessage.createdAt && !newMessage.created_at) {
          newMessage.createdAt = new Date().toISOString();
        }

        state.messages.push(newMessage);
      } else {
      }
    },
    makeMessageSeen: (state, action) => {
      state.messages = state.messages.map((message) =>
        message._id === action.payload ? { ...message, seen: true } : message
      );
    },
    chatLogout: (state) => {
      removeCookie("jwtToken");
      state.jwtToken = null;
      state.messages = [];
      state.socketConnection = false;
    },
    addAgentsOnline: (state, action) => {
      state.agentsOnline = action.payload;
    },
    loadMessages: (state, action) => {
      const messages = action.payload;

      // Filter messages to ensure they belong to current user's chat
      if (Array.isArray(messages)) {
        state.messages = messages;
      } else {
        state.messages = [];
      }

      // Says plainly that this list was fetched rather than spoken. A reader
      // comparing one list to the next cannot tell a conversation of one
      // message being loaded from a question being asked — they look identical
      // — and guessing meant a returning visitor's last question was mirrored
      // out to the card as though they had just typed it.
      state.historyLoads += 1;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.messagesPage = 0;
      state.hasMoreMessages = true;
      state.loadingMoreMessages = false;
    },
    streamStart: (state, action) => {
      state.isTyping = true;
      const agentName = action.payload?.senderName || "Bot";
      state.streamingMessage = {
        _id: "streaming_bot",
        chatId: action.payload?.chatId,
        content: "",
        senderType: "BOT",
        sender: { agent: true, name: agentName, email: "bot@system.com" },
        senderName: agentName,
        createdAt: new Date().toISOString(),
        isStreaming: true,
      };
    },
    streamChunk: (state, action) => {
      if (state.streamingMessage) {
        state.streamingMessage.content = action.payload?.content || "";
      }
    },
    streamEnd: (state, action) => {
      state.isTyping = false;
      const finalMessage = action.payload;
      if (state.streamingMessage && finalMessage?.content) {
        if (!finalMessage._id && finalMessage.messageId) {
          finalMessage._id = finalMessage.messageId;
        }
        if (!finalMessage.createdAt && !finalMessage.created_at) {
          finalMessage.createdAt = new Date().toISOString();
        }
        state.messages.push(finalMessage);
      }
      state.streamingMessage = null;
    },
    addNewMessageWithValidation: (state, action) => {
      const { message, currentChatId, currentUserId } = action.payload;

      // Check if message belongs to current user's chat
      const messageChatId = message.chat?.id || message.chatId;
      const messageSenderId = message.sender?.id || message.senderId;

      // Compare as strings to avoid number/string type mismatches
      const belongsToCurrentChat =
        (messageChatId != null && currentChatId != null && String(messageChatId) === String(currentChatId)) ||
        (messageSenderId != null && currentUserId != null && String(messageSenderId) === String(currentUserId));

      if (!belongsToCurrentChat) {
        return;
      }

      // Ensure the message has a proper ID for React key
      if (!message._id && message.messageId) {
        message._id = message.messageId;
      }

      // Add createdAt if missing
      if (!message.createdAt && !message.created_at) {
        message.createdAt = new Date().toISOString();
      }

      const isRealMessage = message.messageId && !String(message.messageId).startsWith("temp_");

      if (isRealMessage) {
        // Try to replace a matching optimistic temp message (same content, any sender)
        const tempIndex = state.messages.findIndex(
          (msg) =>
            String(msg.messageId).startsWith("temp_") &&
            msg.content === message.content
        );
        if (tempIndex !== -1) {
          // Replace temp with confirmed server message
          state.messages[tempIndex] = message;
          return;
        }

        // Check for exact duplicate by real messageId
        const existingIdx = state.messages.findIndex(
          (msg) => String(msg.messageId) === String(message.messageId)
        );
        if (existingIdx !== -1) {
          if (
            message.sender?.agent !== undefined &&
            state.messages[existingIdx].sender?.agent === undefined
          ) {
            state.messages[existingIdx] = message;
          }
          return;
        }
      }

      state.messages.push(message);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(jwtToken.fulfilled, (state, action) => {
      state.jwtToken = action.payload.token;
      setCookie("jwtToken", action.payload.token, 1);
    });
    builder.addCase(fetchAllMessages.fulfilled, (state, action) => {
      if (action.payload && action.payload.messages) {
        // Newest first from the API — the same order the paginated fetch below
        // reverses. This one did not, so a reloaded conversation read
        // backwards: the answer above the question that caused it, yesterday
        // below today. Measured against the dev API on 2026-09-21, which
        // returns createdAt descending whether or not a sort is asked for.
        state.messages = keepCarriedProducts(
          state.messages,
          [...action.payload.messages].reverse()
        );
      } else {
        debugLog("No messages found in fetchAllMessages response");
        state.messages = [];
      }
      // Fetched, not spoken: see loadMessages. Only that path used to say so,
      // and a conversation reloaded through this one handed the card its last
      // stored answer as new, which moved the shop back to that answer's
      // results on every page load.
      state.historyLoads += 1;
    });
    builder.addCase(fetchAllMessages.rejected, (state, action) => {});

    // Paginated fetch
    builder.addCase(fetchMessages.pending, (state) => {
      state.loadingMoreMessages = true;
    });
    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      state.loadingMoreMessages = false;
      const payload = action.payload;
      // Spring Boot Page response: { content: [...], totalPages, number, last, ... }
      // Or simple: { messages: [...] }
      const newMessages = payload?.content || payload?.messages || [];
      const page = action.meta.arg.page || 0;

      // Fetched, not spoken, whichever page: see loadMessages.
      state.historyLoads += 1;

      if (page === 0) {
        // Initial load — replace messages (newest first from API, reverse to chronological)
        state.messages = [...newMessages].reverse();
        state.messagesPage = 0;
      } else {
        // Older messages — prepend (API returns newest first, reverse to chronological)
        const reversed = [...newMessages].reverse();
        // Deduplicate by messageId
        const existingIds = new Set(state.messages.map(m => String(m.messageId || m._id)));
        const unique = reversed.filter(m => !existingIds.has(String(m.messageId || m._id)));
        state.messages = [...unique, ...state.messages];
        state.messagesPage = page;
      }

      // Check if there are more pages
      if (payload?.last !== undefined) {
        state.hasMoreMessages = !payload.last;
      } else {
        state.hasMoreMessages = newMessages.length >= (action.meta.arg.size || 30);
      }
    });
    builder.addCase(fetchMessages.rejected, (state) => {
      state.loadingMoreMessages = false;
    });
  },
});

export default ChatSocketSlice.reducer;

export const {
  startTyping,
  stopTyping,
  socketConnected,
  socketDisconnected,
  checkConnectionStatus,
  addNewMessage,
  addNewMessageWithValidation,
  makeMessageSeen,
  chatLogout,
  addAgentsOnline,
  loadMessages,
  clearMessages,
  streamStart,
  streamChunk,
  streamEnd,
} = ChatSocketSlice.actions;

export const chatSocketSelector = (state) => state.chatSocket;
